import { ChangeEvent, useEffect, useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { Grid } from '@mui/system';
import { debounce } from 'lodash';
import { isEmpty } from 'validate.js';
import { FormDialog } from 'components';
import CustomTextField from 'components/CustomTextField';
import { KEEP_NOTES } from 'pages/ValuationsAllocation/common/constants/valuations/valuations';
import { useFormValidation } from 'services/hooks/useFormValidation';
import { NewVersionFormProps, NewVersionFormValues } from './NewVersionForm.props';

const defaultConstraints = {
  name: {
    presence: { allowEmpty: false, message: 'is required' },
    length: { minimum: 3, message: 'must be at least 3 characters' },
  },
};

const NewVersionForm = ({ defaultValues, onClose, onFormChange, onSave, open }: NewVersionFormProps) => {
  const { formValidated, validateForm } = useFormValidation(defaultConstraints);
  const [formValues, setFormValues] = useState<NewVersionFormValues>(defaultValues);

  useEffect(() => {
    if (!isEmpty(formValues)) {
      validateForm(formValues);
    }
  }, [formValues, validateForm]);

  const onInputChange = debounce(() => {
    onFormChange(formValidated);
  }, 0);

  useEffect(() => {
    onInputChange();
  }, [onInputChange, onFormChange, formValidated]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist();
    const {
      target: { name: fieldName, value, type, checked },
    } = event;
    setFormValues({
      ...formValues,
      [fieldName]: type === 'checkbox' ? checked : value,
    });
  };

  const onBlur = () => {
    if (!formValues?.name?.length) {
      setFormValues({
        ...formValues,
        name: defaultValues.name,
      });
    }
  };

  return (
    <FormDialog
      open={open}
      title="Save as New Version"
      editMode={false}
      onClose={onClose}
      onSave={onSave}
      isValid={formValidated.isValid}>
      <Grid container>
        <CustomTextField
          fullWidth
          id="name"
          name="name"
          type="text"
          label="Nickname"
          onChange={handleChange}
          value={formValues.name ?? ''}
          onBlur={onBlur}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formValues.keep_notes ?? true}
              onChange={handleChange}
              name="keep_notes"
            />
          }
          label={KEEP_NOTES}
        />
      </Grid>
    </FormDialog>
  );
};

export default NewVersionForm;
